/**
 * EDV Umzug Page - Datenmigration von Butler21 und anderen Altsystemen
 * Unterstützt CSV, XLS, XLSX, PDF, Screenshots für KI-gestützte Migration
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileText, FileSpreadsheet, Image, Mic, MicOff,
  Loader2, CheckCircle, XCircle, AlertTriangle, Sparkles, Play,
  Trash2, Download, ChevronRight, Users, RefreshCw, Eye, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Card, CardContent, CardHeader, CardTitle
} from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Datei-Icon basierend auf Typ
const FileIcon = ({ type }) => {
  const ext = type?.toLowerCase();
  if (['.csv', '.xls', '.xlsx'].includes(ext)) {
    return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
  }
  if (['.jpg', '.jpeg', '.png', '.webp'].includes(ext)) {
    return <Image className="w-5 h-5 text-blue-500" />;
  }
  return <FileText className="w-5 h-5 text-amber-500" />;
};

// Status Badge
const StatusBadge = ({ status }) => {
  const config = {
    hochgeladen: { label: 'Hochgeladen', color: 'bg-slate-100 text-slate-700' },
    wird_analysiert: { label: 'Wird analysiert...', color: 'bg-blue-100 text-blue-700' },
    analysiert: { label: 'Analysiert', color: 'bg-green-100 text-green-700' },
    importiert: { label: 'Importiert', color: 'bg-purple-100 text-purple-700' },
    fehler: { label: 'Fehler', color: 'bg-red-100 text-red-700' }
  };
  const cfg = config[status] || { label: status, color: 'bg-slate-100' };
  return <Badge className={cfg.color}>{cfg.label}</Badge>;
};

export default function EdvUmzugPage() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [klienten, setKlienten] = useState([]);
  
  // Analyse-Dialog
  const [analyseDialog, setAnalyseDialog] = useState({ open: false, upload: null, result: null });
  const [analysing, setAnalysing] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Sprachnotiz
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [selectedKlient, setSelectedKlient] = useState('');
  const [processingVoice, setProcessingVoice] = useState(false);
  const [voiceResult, setVoiceResult] = useState(null);
  
  const fileInputRef = useRef(null);

  // Daten laden
  const loadData = async () => {
    setLoading(true);
    try {
      const [uploadsRes, statusRes, klientenRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/edv-umzug/uploads`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/edv-umzug/status`).then(r => r.json()),
        betreuungAPI.listKlienten()
      ]);
      setUploads(Array.isArray(uploadsRes) ? uploadsRes : []);
      setMigrationStatus(statusRes);
      setKlienten(Array.isArray(klientenRes) ? klientenRes : []);
    } catch (e) {
      console.error('Fehler beim Laden:', e);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Datei hochladen
  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    
    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('datei', file);
        formData.append('typ', 'klienten');
        formData.append('beschreibung', '');
        
        const res = await fetch(`${BACKEND_URL}/api/edv-umzug/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (res.ok) {
          toast.success(`${file.name} hochgeladen`);
        } else {
          const err = await res.json();
          toast.error(`Fehler: ${err.detail || 'Upload fehlgeschlagen'}`);
        }
      } catch (e) {
        toast.error(`Fehler bei ${file.name}`);
      }
    }
    
    setUploading(false);
    loadData();
  };

  // Upload löschen
  const handleDelete = async (uploadId) => {
    if (!window.confirm('Upload wirklich löschen?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/edv-umzug/uploads/${uploadId}`, { method: 'DELETE' });
      toast.success('Gelöscht');
      loadData();
    } catch (e) {
      toast.error('Fehler beim Löschen');
    }
  };

  // KI-Analyse starten
  const handleAnalyse = async (upload) => {
    setAnalyseDialog({ open: true, upload, result: null });
    setAnalysing(true);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/edv-umzug/analyse/${upload.id}`, {
        method: 'POST'
      });
      const result = await res.json();
      
      if (result.success) {
        setAnalyseDialog(prev => ({ ...prev, result: result.analyse }));
        toast.success('Analyse abgeschlossen');
        loadData();
      } else {
        toast.error(result.detail || 'Analyse fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Analyse-Fehler');
    } finally {
      setAnalysing(false);
    }
  };

  // Daten importieren
  const handleImport = async () => {
    if (!analyseDialog.upload) return;
    setImporting(true);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/edv-umzug/import/${analyseDialog.upload.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const result = await res.json();
      
      if (result.success) {
        toast.success(`${result.importiert} Datensätze importiert, ${result.aufgaben_erstellt} Aufgaben erstellt`);
        setAnalyseDialog({ open: false, upload: null, result: null });
        loadData();
      } else {
        toast.error(result.detail || 'Import fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Import-Fehler');
    } finally {
      setImporting(false);
    }
  };

  // Sprachaufnahme starten
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        setAudioChunks(chunks);
        stream.getTracks().forEach(track => track.stop());
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
      toast.info('Aufnahme läuft...');
    } catch (e) {
      toast.error('Mikrofon-Zugriff verweigert');
    }
  };

  // Sprachaufnahme stoppen
  const stopRecording = () => {
    if (mediaRecorder && recording) {
      mediaRecorder.stop();
      setRecording(false);
      setMediaRecorder(null);
    }
  };

  // Sprachnotiz verarbeiten
  const processVoiceNote = async () => {
    if (audioChunks.length === 0) return;
    
    setProcessingVoice(true);
    setVoiceResult(null);
    
    try {
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio', audioBlob, 'sprachnotiz.webm');
      
      if (selectedKlient) {
        const klient = klienten.find(k => k.id === selectedKlient);
        formData.append('klient_id', selectedKlient);
        formData.append('klient_name', klient ? `${klient.vorname} ${klient.nachname}` : '');
      }
      
      const res = await fetch(`${BACKEND_URL}/api/edv-umzug/sprachnotiz`, {
        method: 'POST',
        body: formData
      });
      
      const result = await res.json();
      
      if (result.success) {
        setVoiceResult(result);
        toast.success('Sprachnotiz verarbeitet');
        setAudioChunks([]);
      } else {
        toast.error(result.detail || 'Verarbeitung fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Fehler bei Sprachnotiz');
    } finally {
      setProcessingVoice(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="edv-umzug-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-slate-400 hover:text-slate-600">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Download className="w-6 h-6 text-indigo-500" />
              EDV Umzug
            </h1>
            <p className="text-slate-500 text-sm">Datenmigration von Butler21 und anderen Systemen</p>
          </div>
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Status-Übersicht */}
      {migrationStatus && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <p className="text-sm text-slate-500">Uploads</p>
            <p className="text-2xl font-bold text-slate-800">{migrationStatus.uploads_gesamt}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <p className="text-sm text-slate-500">Analysiert</p>
            <p className="text-2xl font-bold text-green-600">{migrationStatus.uploads_analysiert}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <p className="text-sm text-slate-500">Importiert</p>
            <p className="text-2xl font-bold text-purple-600">{migrationStatus.uploads_importiert}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <p className="text-sm text-slate-500">Datensätze</p>
            <p className="text-2xl font-bold text-blue-600">{migrationStatus.datensaetze_importiert}</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <p className="text-sm text-slate-500">Aufgaben erstellt</p>
            <p className="text-2xl font-bold text-amber-600">{migrationStatus.aufgaben_erstellt}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Linke Spalte: Upload & Liste */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upload-Bereich */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Dateien hochladen
            </h2>
            
            <div
              className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              onDrop={(e) => { e.preventDefault(); handleUpload(e.dataTransfer.files); }}
              onDragOver={(e) => e.preventDefault()}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".csv,.xls,.xlsx,.pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={(e) => { handleUpload(e.target.files); e.target.value = ''; }}
              />
              
              {uploading ? (
                <Loader2 className="w-10 h-10 mx-auto text-indigo-500 animate-spin mb-2" />
              ) : (
                <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
              )}
              
              <p className="text-slate-600 font-medium">
                Dateien hierher ziehen oder klicken
              </p>
              <p className="text-sm text-slate-400 mt-1">
                CSV, Excel, PDF, Screenshots (JPG, PNG)
              </p>
            </div>
          </div>

          {/* Upload-Liste */}
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">
              Hochgeladene Dateien ({uploads.length})
            </h2>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : uploads.length > 0 ? (
              <div className="space-y-3">
                {uploads.map((upload) => (
                  <div
                    key={upload.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-slate-100 hover:bg-slate-50"
                    data-testid={`upload-${upload.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileIcon type={upload.dateityp} />
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">{upload.dateiname}</p>
                        <p className="text-xs text-slate-400">
                          {(upload.dateigroesse / 1024).toFixed(1)} KB · {new Date(upload.created_date).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <StatusBadge status={upload.status} />
                      
                      {upload.status === 'hochgeladen' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAnalyse(upload)}
                        >
                          <Sparkles className="w-4 h-4 mr-1" />
                          Analysieren
                        </Button>
                      )}
                      
                      {upload.status === 'analysiert' && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setAnalyseDialog({
                              open: true,
                              upload,
                              result: upload.analyse_ergebnis
                            });
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Anzeigen
                        </Button>
                      )}
                      
                      {upload.status === 'importiert' && (
                        <Badge className="bg-purple-100 text-purple-700">
                          {upload.importierte_datensaetze} importiert
                        </Badge>
                      )}
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(upload.id)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-2" />
                <p>Noch keine Dateien hochgeladen</p>
              </div>
            )}
          </div>
        </div>

        {/* Rechte Spalte: Sprachnotizen */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Mic className="w-5 h-5" />
              Sprachnotiz
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Sprechen Sie Informationen zu einem Klienten ein. Die KI extrahiert automatisch relevante Daten.
            </p>
            
            {/* Klient auswählen */}
            <div className="mb-4">
              <Label className="text-xs text-slate-600">Klient (optional)</Label>
              <Select value={selectedKlient || 'none'} onValueChange={(v) => setSelectedKlient(v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Klient auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Neuer Klient --</SelectItem>
                  {klienten.map((k) => (
                    <SelectItem key={k.id} value={k.id}>
                      {k.vorname} {k.nachname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Aufnahme-Buttons */}
            <div className="flex gap-2 mb-4">
              {!recording ? (
                <Button
                  onClick={startRecording}
                  className="flex-1 bg-red-500 hover:bg-red-600"
                  disabled={processingVoice}
                >
                  <Mic className="w-4 h-4 mr-2" />
                  Aufnahme starten
                </Button>
              ) : (
                <Button
                  onClick={stopRecording}
                  variant="outline"
                  className="flex-1 border-red-500 text-red-500"
                >
                  <MicOff className="w-4 h-4 mr-2" />
                  Aufnahme stoppen
                </Button>
              )}
            </div>
            
            {/* Verarbeiten */}
            {audioChunks.length > 0 && !recording && (
              <div className="space-y-2">
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Aufnahme bereit
                </Badge>
                <Button
                  onClick={processVoiceNote}
                  className="w-full"
                  disabled={processingVoice}
                >
                  {processingVoice ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Mit KI verarbeiten
                </Button>
              </div>
            )}
            
            {/* Ergebnis */}
            {voiceResult && (
              <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                <p className="text-xs font-medium text-indigo-700 mb-2">Erkannte Informationen:</p>
                <p className="text-sm text-indigo-900 mb-2">
                  {voiceResult.extrahierte_daten?.zusammenfassung || voiceResult.transkript}
                </p>
                {voiceResult.extrahierte_daten?.updates && Object.keys(voiceResult.extrahierte_daten.updates).length > 0 && (
                  <div className="text-xs text-indigo-600">
                    <p className="font-medium">Aktualisierte Felder:</p>
                    <ul className="list-disc ml-4">
                      {Object.entries(voiceResult.extrahierte_daten.updates).map(([k, v]) => (
                        v && <li key={k}>{k}: {v}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Hilfe */}
          <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
            <h3 className="font-semibold text-indigo-800 mb-2">So funktioniert's</h3>
            <ol className="text-sm text-indigo-700 space-y-2 list-decimal ml-4">
              <li>Laden Sie Ihre Butler21-Exportdateien hoch (CSV, Excel)</li>
              <li>Klicken Sie auf "Analysieren" - die KI erkennt die Datenstruktur</li>
              <li>Prüfen Sie die Vorschau und starten Sie den Import</li>
              <li>Für jeden importierten Klienten wird automatisch eine Aufgabe mit Checkliste erstellt</li>
              <li>Nutzen Sie Sprachnotizen, um zusätzliche Details zu ergänzen</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Analyse-Anzeige (Inline) */}
      {analyseDialog.open && (
        <Card className="mt-6 border-indigo-200 shadow-lg" data-testid="analyse-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-500" />
                KI-Analyse: {analyseDialog.upload?.dateiname}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setAnalyseDialog({ open: false, upload: null, result: null })}>
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
          {analysing ? (
            <div className="py-12 text-center">
              <Loader2 className="w-10 h-10 mx-auto text-indigo-500 animate-spin mb-4" />
              <p className="text-slate-600">Analysiere Datei mit KI...</p>
              <p className="text-sm text-slate-400">Dies kann einen Moment dauern</p>
            </div>
          ) : analyseDialog.result ? (
            <div className="space-y-4">
              {/* Zusammenfassung */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-700 mb-2">Zusammenfassung</p>
                <p className="text-slate-600">{analyseDialog.result.zusammenfassung}</p>
              </div>
              
              {/* Statistiken */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {analyseDialog.result.anzahl_datensaetze || '?'}
                  </p>
                  <p className="text-xs text-blue-500">Datensätze</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {analyseDialog.result.erkannte_felder?.length || 0}
                  </p>
                  <p className="text-xs text-green-500">Felder erkannt</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${
                  analyseDialog.result.importierbar ? 'bg-green-50' : 'bg-amber-50'
                }`}>
                  {analyseDialog.result.importierbar ? (
                    <CheckCircle className="w-6 h-6 mx-auto text-green-500 mb-1" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 mx-auto text-amber-500 mb-1" />
                  )}
                  <p className={`text-xs ${analyseDialog.result.importierbar ? 'text-green-500' : 'text-amber-500'}`}>
                    {analyseDialog.result.importierbar ? 'Importierbar' : 'Prüfung nötig'}
                  </p>
                </div>
              </div>
              
              {/* Erkannte Felder */}
              {analyseDialog.result.erkannte_felder?.length > 0 && (
                <div>
                  <p className="font-medium text-slate-700 mb-2">Erkannte Spalten</p>
                  <div className="flex flex-wrap gap-1">
                    {analyseDialog.result.erkannte_felder.map((feld, i) => (
                      <Badge key={i} variant="outline">{feld}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Mapping */}
              {analyseDialog.result.mapping_vorschlag && Object.keys(analyseDialog.result.mapping_vorschlag).length > 0 && (
                <div>
                  <p className="font-medium text-slate-700 mb-2">Feld-Zuordnung</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(analyseDialog.result.mapping_vorschlag).map(([von, nach]) => (
                      <div key={von} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <span className="text-slate-500">{von}</span>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                        <span className="font-medium text-slate-700">{nach}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Hinweise */}
              {analyseDialog.result.hinweise?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="font-medium text-amber-700 mb-1">Hinweise</p>
                  <ul className="text-sm text-amber-600 list-disc ml-4">
                    {analyseDialog.result.hinweise.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
              )}
              
              {/* Vorschau */}
              {analyseDialog.result.vorschau_daten?.length > 0 && (
                <div>
                  <p className="font-medium text-slate-700 mb-2">
                    Vorschau (erste {Math.min(5, analyseDialog.result.vorschau_daten.length)} Zeilen)
                  </p>
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          {Object.keys(analyseDialog.result.vorschau_daten[0] || {}).slice(0, 6).map((key) => (
                            <th key={key} className="px-3 py-2 text-left font-medium text-slate-600">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {analyseDialog.result.vorschau_daten.slice(0, 5).map((row, i) => (
                          <tr key={i} className="border-t">
                            {Object.values(row).slice(0, 6).map((val, j) => (
                              <td key={j} className="px-3 py-2 text-slate-700 truncate max-w-[150px]">
                                {val || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <div className="flex justify-end gap-2 pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => setAnalyseDialog({ open: false, upload: null, result: null })}
            >
              Schließen
            </Button>
            {analyseDialog.result?.importierbar && analyseDialog.upload?.status !== 'importiert' && (
              <Button onClick={handleImport} disabled={importing}>
                {importing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Users className="w-4 h-4 mr-2" />
                )}
                Daten importieren
              </Button>
            )}
          </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
